import { useContext, useState } from 'react';
import qz from 'qz-tray';
import postal from 'postal';

import config from '../config';
import ApplicationContext from '../ApplicationContext';
import Select from '../ui/Select';
import useReportViewer from './useReportViewer';
import useSuntekSettings from './useSuntekSettings';
import useGlobalOkDialog from './useGlobalOkDialog';
import useGlobalYesNoDialog from './useGlobalYesNoDialog';

/**
 * QZ Tray 를 통한 Direct Print.
 *
 * 3.3 은 ClientSetup(PortalUser.Setup)에 문서 종류별 프린터를 저장하고
 * FrmPrintPreview 에서 미리보기 없이 바로 인쇄했다. 웹에서는 브라우저가
 * 로컬 프린터에 직접 접근할 수 없으므로 QZ Tray(로컬 데몬)를 경유한다.
 *
 * 흐름: report/printpdf 로 PDF 를 받아 base64 로 QZ 에 넘긴다.
 */

const PrinterStorageKeyPrefix = 'ifms5.qz.printer.';

/**
 * 3.3 Client Setup 의 프린터 목록과 같은 구성/순서.
 * Hawaii BL 만 뺐다 — ifms5 에는 그 DocumentType 자체가 없어서 인쇄할 화면이 없다.
 * key 는 FmsClientOption 의 PrtXxx 속성명에서 Prt 를 뗀 것이라, 나중에 T_USER 나
 * ClientSetup 으로 옮길 때 그대로 매핑된다.
 * label 은 3.3 ClientSetupView 의 화면 문구를 그대로 쓴다.
 */
export const PRINTER_DOC_TYPES: { key: string, label: string }[] = [
  { key: 'AirWayBillM', label: 'Air Waybill (M)' },
  { key: 'AirWayBillH', label: 'Air Waybill (H)' },
  { key: 'OceanBL', label: 'Ocean B/L' },
  { key: 'Check', label: 'Check' },
  { key: 'IT', label: 'I.T.' },
  { key: 'DO', label: 'D.O.' },
  { key: 'Label', label: 'Label' },
  { key: 'FolderLabel', label: 'Folder Label' },
  { key: 'BankDraft', label: 'Bank Draft' },
  { key: 'AirLabel', label: 'Air Label' },
];

/**
 * 프린터 이름은 그 PC 에 설치된 드라이버 이름이라 본질적으로 PC 종속이다.
 * (예: "Samsung Samsung K6400 (203.234.215.165)")
 * 3.3 처럼 사용자 단위로 저장하면 다른 PC 에서 없는 프린터를 가리키게 되므로
 * 1차 저장은 PC 단위(localStorage)로 둔다.
 * 서버 백업(T_USER)은 캐시 삭제 대비로 추후 추가 예정.
 */
export function getSavedPrinter(docKey: string): string | null {
  try {
    return localStorage.getItem(PrinterStorageKeyPrefix + docKey);
  } catch {
    return null;
  }
}

/**
 * 260918-hykim 문서별 ON/OFF.
 * Air Label 은 맞는데 D.O. 는 양식지 위치가 안 맞는 식으로 문서마다 사정이 다를 수 있다.
 * "어느 프린터로" 와 "바로 인쇄할까" 는 다른 질문이라 드롭다운에 섞지 않고 따로 둔다.
 * 꺼도 프린터 선택값은 남는다 — 문제를 고친 뒤 다시 켜면 그대로 쓴다.
 */
const DocDirectPrintKeyPrefix = 'ifms5.qz.direct.';

export function isDocDirectPrint(docKey: string): boolean {
  try {
    return localStorage.getItem(DocDirectPrintKeyPrefix + docKey) !== 'off';
  } catch {
    return true;
  }
}

export function setDocDirectPrint(docKey: string, on: boolean): void {
  try {
    localStorage.setItem(DocDirectPrintKeyPrefix + docKey, on ? 'on' : 'off');
  } catch {
    /* 사생활 보호 모드 등에서 실패할 수 있다. */
  }
}

/*
 * 260918-hykim 이 PC 에서만 끄는 스위치.
 * Use Direct Print 는 우리(썬텍)가 테넌트 단위로 켜 주는 설정이라, 고객이 설치까지 했는데
 * 양식지 위치가 안 맞는 것 같으면 스스로 되돌릴 방법이 없다. 프린터를 비우면 이제 오류가
 * 나므로 더 막힌다. 급한 대로 예전 미리보기로 돌아갈 수 있게 PC 단위 스위치를 둔다.
 * 기본값은 켜짐 — 설정을 켜 준 고객은 바로 쓸 수 있어야 한다.
 */
const DirectPrintOnThisPcKey = 'ifms5.qz.useDirectPrint';

export function isDirectPrintOnThisPc(): boolean {
  try {
    return localStorage.getItem(DirectPrintOnThisPcKey) !== 'off';
  } catch {
    return true;
  }
}

export function setDirectPrintOnThisPc(on: boolean): void {
  try {
    localStorage.setItem(DirectPrintOnThisPcKey, on ? 'on' : 'off');
  } catch {
    /* 사생활 보호 모드 등에서 실패할 수 있다. */
  }
}

export function saveSelectedPrinter(docKey: string, printerName: string): void {
  try {
    localStorage.setItem(PrinterStorageKeyPrefix + docKey, printerName);
  } catch {
    /* 사생활 보호 모드 등에서 실패할 수 있다. 저장 실패는 무시한다. */
  }
}

/** report/printpdf 응답. 페이지 범위는 QZ pageRanges 형식("1-2"), 비어 있으면 해당 없음. */
interface DirectPrintPdf {
  pdf: string,
  formPages: string,
  attachPages: string,
  attachPageCount: number,
}

/**
 * 'printed'  : 인쇄함
 * 'preview'  : Direct Print 를 쓰지 않기로 한 문서 -> 호출한 쪽이 기존 미리보기를 띄운다
 *              (설정이 꺼졌거나, 이 PC 를 껐거나, 그 문서를 껐을 때)
 * 'cancelled': 사용자가 인쇄 대화상자를 취소함
 * 'busy'     : 앞의 인쇄가 아직 진행 중이라 무시함
 */
export type DirectPrintResult = 'printed' | 'preview' | 'cancelled' | 'busy';

/*
 * 인쇄는 연결/서명/PDF 생성까지 몇 초 걸리는데, 그 사이 버튼을 다시 누르면 수표가 두 번 나간다.
 * 화면이 여러 개 떠 있어도 QZ 연결은 하나라 모듈 단위로 막는다.
 */
let printInProgress = false;

/*
 * 왜 기다리게 되는가 (2026-09-17 크롬 실측):
 *   - QZ 가 있으면 연결은 약 40ms 다.
 *   - 없으면 크롬이 죽은 로컬 포트 하나마다 약 2.1초를 쓴다. qz-tray 는 포트를 4~8개
 *     "순서대로" 두드리므로 9~19초가 걸렸다.
 *   - 같은 포트들을 동시에 열면 4개가 합쳐서 2.06초다. 즉 대기 시간은 포트 수가 아니라
 *     한 번의 고정 대기다.
 * 그래서 포트 목록은 건드리지 않고(비표준 포트를 쓰는 PC 도 계속 찾을 수 있게) 화면 쪽에서만
 * 짧게 끊는다. 여기서 raw WebSocket 으로 직접 포트를 떠보는 방법은 쓰지 않는다 —
 * 서명 없는 연결이라 QZ 가 익명 요청으로 보고 허용 창을 띄운다(직접 겪음).
 */

/** 화면 판별용. 이 안에 안 붙으면 일단 "없음"으로 그리고, 뒤늦게 붙으면 그때 고친다. */
const ProbeAnswerMs = 1200;

/** 인쇄 버튼용. 여기서 못 붙으면 기존 미리보기로 넘어간다. */
const PrintConnectMs = 3000;

/** 연결 시도는 한 번만 돈다. 화면 두 곳에서 동시에 불러도 같은 약속을 나눠 쓴다. */
let connectAttempt: Promise<void> | null = null;

/*
 * qz.websocket.isActive() 를 "쓸 수 있다"로 보면 안 된다 — 소켓이 아직 CONNECTING 이어도 true 다
 * (qz-tray.js 의 _qz.tools.isActive). 그 사이에 qz.printers.find() 를 부르면 아직 붙지 않은
 * 소켓의 sendData 를 찾다가 "connection.sendData is not a function" 이 난다.
 * 그래서 connect() 가 끝난 뒤에만 켜지는 우리 플래그를 따로 둔다.
 */
let connectionReady = false;

function withTimeout<T>(work: Promise<T>, ms: number): Promise<T | 'timeout'> {
  return Promise.race([
    work,
    new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), ms)),
  ]);
}

/**
 * 'ok'         : QZ 가 돌고 있어서 프린터를 고를 수 있다
 * 'notRunning' : QZ Tray 가 없거나 꺼져 있다
 *
 * 인증서 등록 여부는 가리지 않는다. QZ 가 신뢰 여부를 알려주는 API 가 없어서 걸린 시간으로
 * 추측할 수밖에 없는데, 그 경우는 인쇄할 때 허용 창이 뜨는 것으로 사용자가 바로 알 수 있다.
 * (설치 파일을 한 번 더 실행하면 인증서만 등록된다 — Client Setup 안내 문구 참고)
 */
export type QzState = 'ok' | 'notRunning';

export interface QzProbeResult { state: QzState, printers: string[] }

/** 한 번 받아온 프린터 목록. 화면을 다시 열 때 다시 묻지 않는다(F5 하면 초기화된다). */
let printerCache: string[] | null = null;

/** 오류 문구에 쓰는 문서 이름. 3.3 ModAR 의 str2 와 같은 값이다. */
function docLabel(docKey: string): string {
  return PRINTER_DOC_TYPES.find(d => d.key === docKey)?.label ?? docKey;
}

function isCancelError(e: any): boolean {
  // 사용자가 프린터 대화상자를 취소하면 QZ 가 "Printing cancelled" 를 던진다.
  return /cancel/i.test(String(e?.message ?? e));
}

/** 3.3 FrmMsgPrintAttach 에 해당하는 첨부 페이지 프린터 선택. */
function AttachPrinterPicker({ message, printers, defaultPrinter, onSelect }: {
  message: string,
  printers: string[],
  defaultPrinter: string,
  onSelect: (printer: string) => void,
}) {
  const [value, setValue] = useState(defaultPrinter);

  return (
    <div>
      <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
      <div style={{ width: '320px', marginTop: '8px' }}>
        <Select
          selectId="qz-attach-printer"
          value={value}
          items={printers.map(p => ({ label: p, value: p }))}
          onChange={(e: any) => {
            const printer = e?.target?.value ?? '';
            setValue(printer);
            onSelect(printer);
          }}
        />
      </div>
    </div>
  );
}

/*
 * 서명이 없으면 QZ 가 'anonymous request' 로 취급해서 연결/조회/인쇄 매 건마다
 * 사용자에게 허용 창을 띄우고, Remember 체크박스도 비활성화된다. 실무 사용 불가.
 *
 * 인증서와 개인키 모두 서버가 갖는다. 인증서는 공개 정보지만 빌드에 박아두면
 * 교체할 때마다 프론트를 다시 빌드해야 하므로 report/qzcert 로 받아온다.
 * 서명은 report/qzsign 이 한다 — 개인키가 브라우저에 있으면 누구나 우리 인증서
 * 이름으로 인쇄 요청을 만들 수 있다.
 *
 * 서버에 인증서/키가 없으면 404 가 오고, 그때는 서명 없이 동작한다(허용 창이 뜬다).
 */
let securityConfigured = false;

function configureSecurity(appContext: any): void {
  if (securityConfigured) return;
  securityConfigured = true;

  qz.security.setCertificatePromise((resolve: (v: string) => void, reject: (e: any) => void) => {
    fetch(`${config.serverUrl}report/qzcert`, {
      headers: { Authorization: appContext.api.getAuthKey() },
    })
      .then(res => (res.ok ? res.text() : Promise.reject(new Error(`no certificate (${res.status})`))))
      .then(resolve)
      .catch(reject);
  });

  qz.security.setSignatureAlgorithm('SHA512');

  // 연결이 끊기면 다시 붙어야 하고, 프린터 목록도 그 PC 상태가 바뀌었을 수 있으니 버린다.
  qz.websocket.setClosedCallbacks(() => { connectionReady = false; printerCache = null; });

  qz.security.setSignaturePromise((toSign: string) =>
    (resolve: (v: string) => void, reject: (e: any) => void) => {
      fetch(`${config.serverUrl}report/qzsign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: appContext.api.getAuthKey(),
        },
        body: JSON.stringify({ toSign }),
      })
        .then(res => (res.ok ? res.text() : Promise.reject(new Error(`sign failed (${res.status})`))))
        .then(resolve)
        .catch(reject);
    });
}

export default function useQzPrint() {
  const appContext = useContext(ApplicationContext);
  const suntekSettings = useSuntekSettings();
  const { viewReport } = useReportViewer();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const channel = postal.channel('ajax');

  /*
   * 260916-hykim Direct Print 는 세 층으로 켜고 끈다. 하나라도 꺼지면 그 문서는 미리보기로 간다.
   *   고객사   Suntek Setup > General 의 "Use Direct Print (QZ Tray)" (T_SETTINGS.UseDirectPrint)
   *   이 PC    Client Setup 제목 줄의 토글          (localStorage)
   *   문서 하나 Client Setup 줄마다 있는 토글        (localStorage)
   * 위 둘이 directPrintEnabled, 셋 다 합친 것이 printsDirectly 다.
   */
  /** 고객사 설정(우리가 켜 준다). 이게 꺼져 있으면 Client Setup 에 프린터 영역이 아예 안 보인다. */
  const tenantDirectPrint: boolean = suntekSettings.general?.useDirectPrint ?? false;

  /** 실제로 바로 인쇄할지. 고객이 이 PC 에서 꺼 두면 예전 미리보기로 돌아간다. */
  const directPrintEnabled: boolean = tenantDirectPrint && isDirectPrintOnThisPc();

  /**
   * 이 문서를 지금 누르면 바로 인쇄되는가. 화면 버튼 문구(PRINT / PREVIEW)를 이걸로 정한다.
   * 프린터를 아직 안 고른 문서는 PRINT 로 둔다 — 눌렀을 때 3.3 처럼 "프린터를 고르세요" 안내가
   * 뜨는 게 맞기 때문이다(설정을 켜 두고 마무리를 안 한 상태).
   * 반대로 사용자가 직접 off 로 돌린 문서는 미리보기가 뜨므로 PREVIEW 다.
   */
  function printsDirectly(docKey: string): boolean {
    return directPrintEnabled && isDocDirectPrint(docKey);
  }

  function isAvailable(): boolean {
    return connectionReady && qz.websocket.isActive();
  }

  function connect(): Promise<void> {
    if (connectionReady && qz.websocket.isActive()) return Promise.resolve();
    if (connectAttempt) return connectAttempt;   // 진행 중인 시도가 있으면 그게 끝나기를 기다린다

    configureSecurity(appContext);
    connectAttempt = (qz.websocket.connect() as Promise<void>)
      .then(() => { connectionReady = true; })
      .finally(() => { connectAttempt = null; });
    return connectAttempt;
  }

  async function listPrinters(): Promise<string[]> {
    await connect();
    return qz.printers.find() as Promise<string[]>;
  }

  /**
   * Client Setup 이 쓰는 상태 판별. QZ 에 붙어서 프린터 목록까지 받아 보는 게 전부다.
   * @param onLateAnswer 1.2초 안에 답이 없으면 일단 'notRunning' 을 돌려주고, 뒤늦게 결과가
   *                     나오면 이 콜백으로 알려 준다(느린 PC 에서 잘못 안내한 채 남지 않도록).
   */
  async function probeQz(onLateAnswer?: (r: QzProbeResult) => void): Promise<QzProbeResult> {
    if (printerCache) return { state: 'ok', printers: printerCache };

    const full: Promise<QzProbeResult> = (async () => {
      try {
        await connect();
        const printers = await (qz.printers.find() as Promise<string[]>);
        printerCache = printers;
        return { state: 'ok' as QzState, printers };
      } catch {
        // 미설치/미기동, 또는 허용 창에서 Block 을 누른 경우
        return { state: 'notRunning' as QzState, printers: [] };
      }
    })();

    const quick = await withTimeout(full, ProbeAnswerMs);
    if (quick !== 'timeout') return quick;

    // 아직 안 붙었다. 없다고 보고 먼저 그린다. 늦게라도 결과가 나오면 화면을 고쳐 준다.
    if (onLateAnswer) full.then(onLateAnswer).catch(() => { /* 화면은 이미 안내로 그려져 있다 */ });
    return { state: 'notRunning', printers: [] };
  }

  /**
   * 저장된 이름이 이 PC 에 실제로 있는지 대조한다. 없는 이름을 그대로 넘기면
   * QZ 는 오류 없이 아무 일도 하지 않는다.
   */
  function resolvePrinter(docKey: string, saved: string, printers: string[]): string {
    if (printers.indexOf(saved) < 0) {
      throw new Error(
        `Could not find ${docLabel(docKey)} form printer.\n`
        + `(The printer name is ${saved})`,
      );
    }

    return saved;
  }

  async function fetchReportPdf(reportArgs: any): Promise<DirectPrintPdf> {
    const response = await fetch(`${config.serverUrl}report/printpdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ args: reportArgs, jwt: appContext.api.getAuthKey() }),
    });
    if (!response.ok) throw new Error(`Failed to build the report (${response.status})`);

    const result: DirectPrintPdf = await response.json();
    if (!result?.pdf) throw new Error('The report is empty.');
    return result;
  }

  /*
   * scaleContent 는 QZ 기본값이 true 라서 PDF 를 용지 크기에 맞춰 늘린다.
   * 수표 용지나 AWB 양식지처럼 미리 인쇄된 칸에 맞춰야 하는 문서는 그만큼 위치가 밀린다.
   * 3.3 은 ActiveReports 가 프린터로 바로 보내서 배율을 건드리지 않았으므로 false 로 맞춘다.
   * 나머지(용지 크기·여백·트레이)는 3.3 처럼 프린터 드라이버 설정을 그대로 따른다.
   */
  function printPages(printer: string, pdf: string, pageRanges: string) {
    return qz.print(qz.configs.create(printer, { scaleContent: false }), [
      { type: 'pixel', format: 'pdf', flavor: 'base64', data: pdf, options: { pageRanges } },
    ]);
  }

  /**
   * 첨부 페이지(AWB 의 IATA 약관/Rider, 수표 명세)를 보낼 프린터를 묻는다. 3.3 MsgPrintAttach 와 같다.
   * 3.3 처럼 기본값은 양식 프린터가 아닌 Windows 기본 프린터다. No 면 첨부는 인쇄하지 않는다.
   * @returns 고른 프린터. 건너뛰면 빈 문자열.
   */
  async function askAttachPrinter(pageCount: number, formPrinter: string, printers: string[]): Promise<string> {
    let defaultPrinter = '';
    try {
      defaultPrinter = await qz.printers.getDefault();
    } catch {
      /* 기본 프린터가 없는 PC */
    }
    if (!defaultPrinter || defaultPrinter === formPrinter || printers.indexOf(defaultPrinter) < 0) {
      defaultPrinter = printers.find(p => p !== formPrinter) ?? formPrinter;
    }

    let selected = defaultPrinter;
    const yes = await showGlobalYesNoDialog(
      <AttachPrinterPicker
        message={`Now printing attached ${pageCount} page(s) ... Please select printer.\nClick No to skip the attached page(s).`}
        printers={printers}
        defaultPrinter={defaultPrinter}
        onSelect={p => { selected = p; }}
      />,
    );

    return yes ? selected : '';
  }

  /**
   * @param reportArgs viewReport 에 넘기는 것과 동일한 ReportArgs
   * @param docKey     프린터를 기억할 문서 구분 키 (예: 'AirLabel')
   *
   * 끄고 켜는 세 층 중 하나라도 꺼져 있으면 'preview' 를 돌려준다(조용히 예전 방식).
   * 켜져 있는데 준비가 안 됐으면 — 프린터 미지정, QZ 미기동 — 던져서 알린다.
   * 3.3 도 그랬다(FrmPrintPreview.cs:1880 에서 메시지를 띄우고 인쇄를 건너뛴다).
   * 조용히 미리보기로 넘기면 왜 바로 안 나가는지 사용자가 알 수 없기 때문이다.
   */
  async function directPrint(reportArgs: any, docKey: string): Promise<DirectPrintResult> {
    // 설정이 꺼져 있으면 Direct Print 를 안 쓰는 고객이므로 예전 그대로 미리보기다.
    if (!directPrintEnabled) return 'preview';

    // 이 문서만 꺼 둔 경우. 사용자가 일부러 고른 것이므로 안내 없이 미리보기로 간다.
    if (!isDocDirectPrint(docKey)) return 'preview';

    // 연타 차단은 안내 창보다 먼저다. 뒤에 두면 프린터 미지정 문서를 연타할 때 창이 여러 개 뜬다.
    if (printInProgress) return 'busy';

    // 여기부터는 "바로 인쇄하겠다" 는 상태다. 준비가 안 됐으면 3.3 과 같은 문구로 알린다
    // (ifms.report5 ModAR.cs:341).
    const saved = getSavedPrinter(docKey);
    if (!saved) {
      throw new Error(
        `No ${docLabel(docKey)} form printer is configured.\n`
        + 'Please select a printer in Settings > Client Setup.',
      );
    }

    printInProgress = true;
    channel.publish('ajax.loadingstart', {});
    try {
      // QZ 가 없는 PC 에서 크롬이 포트마다 2초씩 쓰는 탓에 10초 가까이 멈춰 있다가 미리보기가
      // 뜨던 것을 끊는다. 붙어 있는 PC 는 40ms 라 걸릴 일이 없다.
      const connected = await withTimeout(connect().then(() => true).catch(() => false), PrintConnectMs);
      if (connected !== true) {
        throw new Error(
          'Direct Print is not ready on this computer.\n'
          + 'Please set it up in Settings > Client Setup.',
        );
      }

      const printers = await listPrinters();
      const printer = resolvePrinter(docKey, saved, printers);
      const report = await fetchReportPdf(reportArgs);

      try {
        await printPages(printer, report.pdf, report.formPages);
      } catch (e: any) {
        if (isCancelError(e)) return 'cancelled';
        throw e;
      }

      if (report.attachPageCount > 0) {
        const attachPrinter = await askAttachPrinter(report.attachPageCount, printer, printers);
        if (attachPrinter) {
          channel.publish('ajax.loadingstart', {});
          try {
            await printPages(attachPrinter, report.pdf, report.attachPages);
          } catch (e: any) {
            // 양식은 이미 나갔으므로 첨부 취소는 인쇄 성공으로 본다.
            if (!isCancelError(e)) throw e;
          }
        }
      }

      return 'printed';
    } finally {
      printInProgress = false;
      channel.publish('ajax.loadingend', {});
    }
  }

  /**
   * 화면 공통 흐름: 바로 인쇄하고, 프린터 지정이 없거나 QZ 가 없으면 기존 미리보기, 오류는 메시지로 알린다.
   * @returns 인쇄했거나 미리보기를 띄웠으면 true. 취소/오류/중복 클릭이면 false.
   */
  async function printOrPreview(reportArgs: any, docKey: string): Promise<boolean> {
    try {
      const result = await directPrint(reportArgs, docKey);
      if (result === 'preview') viewReport(reportArgs, "", true);
      return result === 'printed' || result === 'preview';
    } catch (e: any) {
      await showGlobalOkDialog(<p style={{ whiteSpace: 'pre-line' }}>{e?.message ?? 'Failed to print.'}</p>, false, null, 'error');
      return false;
    }
  }

  return { directPrint, printOrPreview, listPrinters, probeQz, connect, isAvailable,
    directPrintEnabled, tenantDirectPrint, printsDirectly };
}
